import { memo, useCallback, useMemo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Box, Button, Flex, Icon, Tag, usePrefersReducedMotion } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { Pencil } from 'lucide-react';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import type { NodeData } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/flowEditorBlockNodeTypes';
import { StaticBlockContent } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/StaticBlockNode/StaticBlockContent';
import { BlockNodeHeader } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/BlockNodeHeader';
import { BLOCK_NODE_WIDTH } from '../../constants';
import { useTiptapFileSrcResolverFromContext } from '@duke-hq/ui/components/DukeTiptapEditor/useTiptapFileSrc';
import { usePersonaResolver } from '@duke-hq/app-admin-web/components/ReactFlow/PersonaResolverContext';
import { useCanvasTranslationContext } from './CanvasTranslationContext';
import { translateNodeContent } from './translatedNodeContent';
import { testIds } from './testIds';

const HANDLE_STYLE = {
  width: '10px',
  height: '10px',
  zIndex: 1001,
};

// Stable empty list so nodes without rows don't defeat the translate memo.
const NO_ROWS: never[] = [];

function TranslatedBlockNodeComponent({ id, type, data, isConnectable }: NodeProps<NodeData>) {
  const { language, dir, rowsByNode, onEditNode } = useCanvasTranslationContext();
  const { getPersonaById } = usePersonaResolver();
  const resolveFileSrc = useTiptapFileSrcResolverFromContext();
  const motionDisabled = usePrefersReducedMotion();

  const rowsForNode = rowsByNode.get(id) ?? NO_ROWS;

  const translated = useMemo(
    () => translateNodeContent({ id, data }, rowsForNode),
    [id, data, rowsForNode],
  );

  const tiptap = translated?.tiptap ?? data.tiptap;
  const options = translated?.options ?? data.options;

  const isStartNode = type === 'start';

  const resolveMediaSrc = useCallback(
    (src: string, fileDrn?: unknown) => resolveFileSrc(fileDrn, `/files/${src}`),
    [resolveFileSrc],
  );
  const renderOptionHandle = useCallback(
    (optionId: string) => (
      <Handle
        type="source"
        position={Position.Right}
        id={optionId}
        style={HANDLE_STYLE}
        isConnectable={isConnectable}
      />
    ),
    [isConnectable],
  );

  return (
    <Flex
      data-testid={testIds.translatedNode}
      flexDir="column"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      bg="white"
      boxShadow="md"
      minW={BLOCK_NODE_WIDTH}
      maxW={BLOCK_NODE_WIDTH}
      m={1}
      pos="relative"
      role="group"
    >
      <BlockNodeHeader
        label={data.label}
        actions={
          onEditNode == null ? undefined : (
            <Button
              size="xs"
              variant="ghost"
              leftIcon={<Icon as={Pencil} boxSize={iconSize.xs} />}
              opacity={0}
              _groupHover={{ opacity: 1 }}
              _focusVisible={{ opacity: 1 }}
              onClick={() => onEditNode(id)}
              data-testid={testIds.nodeEdit}
            >
              <Trans>Edit</Trans>
            </Button>
          )
        }
      />

      {/* dir on the content only: on the node frame it would mirror handles and edge geometry. */}
      <Box
        key={language}
        p={2}
        borderRadius="md"
        dir={dir}
        sx={
          motionDisabled
            ? undefined
            : {
                animation:
                  'translatedContentSwap 260ms ease-out, translatedContentPulse 620ms ease-out',
                '@keyframes translatedContentSwap': {
                  '0%': { opacity: 0, transform: 'translateY(3px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
                '@keyframes translatedContentPulse': {
                  '0%': { backgroundColor: 'var(--chakra-colors-blue-50)' },
                  '100%': { backgroundColor: 'transparent' },
                },
              }
        }
      >
        <StaticBlockContent
          tiptap={tiptap}
          options={options}
          getPersonaById={getPersonaById}
          resolveMediaSrc={resolveMediaSrc}
          renderOptionHandle={renderOptionHandle}
        />
      </Box>

      {!isStartNode ? (
        <Handle
          type="target"
          position={Position.Left}
          style={HANDLE_STYLE}
          isConnectable={isConnectable}
        />
      ) : null}
      {options ? null : (
        <Handle
          type="source"
          position={Position.Right}
          id="b"
          style={HANDLE_STYLE}
          isConnectable={isConnectable}
        />
      )}

      {isStartNode ? (
        <Tag
          position="absolute"
          top={pxToRem(-12)}
          left={1}
          backgroundColor="black"
          color="white"
          textTransform="uppercase"
          fontWeight="bold"
          fontSize="xs"
        >
          Start
        </Tag>
      ) : null}
    </Flex>
  );
}

export const TranslatedBlockNode = memo(TranslatedBlockNodeComponent);
TranslatedBlockNode.displayName = 'TranslatedBlockNode';
